import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

// Use relative URL so requests go through the same domain (proxied by Nginx).
// Fall back to REACT_APP_BACKEND_URL for non-proxied environments (e.g. local dev).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const presetType = searchParams.get('type') || '';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    insuranceType: presetType,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update insurance type if URL ?type= changes (e.g., navigating between product pages)
  useEffect(() => {
    if (presetType) {
      setFormData((prev) => ({ ...prev, insuranceType: presetType }));
    }
  }, [presetType]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleInsuranceTypeChange = (value) => {
    setFormData({
      ...formData,
      insuranceType: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      toast({
        title: "Quote Request Submitted!",
        description: "Thank you for contacting Progress Insurance Solutions. Our team will reach out to you within 24 hours.",
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        insuranceType: '',
        message: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "Please try again later or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Ready to protect what matters most? Get in touch with our team today for a free, personalized insurance quote.
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="border-2 hover:border-[#C9A361] transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#F5F1E8] p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-[#C9A361]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Coral Springs Office</h3>
                      <p className="text-slate-600">
                        3111 N University Dr Ste 404<br />
                        Coral Springs, FL 33065
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-[#C9A361] transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#F5F1E8] p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-[#C9A361]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Maitland Office</h3>
                      <p className="text-slate-600">
                        500 N Maitland Ave Ste 304<br />
                        Maitland, FL 32751
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-[#C9A361] transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#F5F1E8] p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-[#C9A361]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Phone</h3>
                      <a href="tel:954-495-2929" className="text-slate-600 hover:text-[#C9A361] transition-colors">
                        954-495-2929
                      </a>
                      <p className="text-sm text-slate-500 mt-1">Available 24/7</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-[#C9A361] transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#F5F1E8] p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-[#C9A361]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Email</h3>
                      <a href="mailto:contact@progressinsure.com" className="text-slate-600 hover:text-[#C9A361] transition-colors break-all">
                        contact@progressinsure.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-3xl">Request Your Free Quote</CardTitle>
                  <p className="text-slate-600 mt-2">Fill out the form below and our team will contact you shortly.</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="insuranceType">Type of Insurance *</Label>
                      <Select value={formData.insuranceType} onValueChange={handleInsuranceTypeChange} required>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select insurance type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto Insurance</SelectItem>
                          <SelectItem value="home">Homeowners Insurance</SelectItem>
                          <SelectItem value="renters">Renters Insurance</SelectItem>
                          <SelectItem value="flood">Flood Insurance</SelectItem>
                          <SelectItem value="umbrella">Umbrella Insurance</SelectItem>
                          <SelectItem value="commercial">Commercial Insurance</SelectItem>
                          <SelectItem value="liability">General Liability</SelectItem>
                          <SelectItem value="property">Commercial Property</SelectItem>
                          <SelectItem value="workers-comp">Workers' Compensation</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="mt-2"
                        placeholder="Tell us about your insurance needs..."
                      />
                    </div>

                    <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-[#C9A361] hover:bg-[#B8925A] text-white py-6 text-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed">
                      {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>

                    <p className="text-xs text-slate-500 text-center">
                      By submitting this form, you agree to our{' '}
                      <Link to="/privacy-policy" className="text-[#C9A361] hover:underline">Privacy Policy</Link>
                      {' '}and{' '}
                      <Link to="/terms-of-service" className="text-[#C9A361] hover:underline">Terms of Service</Link>.
                      SMS consent is not shared with third parties. Message and data rates may apply. Reply STOP to opt out.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 mb-8 text-center">Visit Our Office</h2>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3572.8282475949!2d-80.2583!3d26.2709!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9035de20e0d7b%3A0x8b6b3e8b8e8b8e8b!2s3111%20N%20University%20Dr%20Ste%20404%2C%20Coral%20Springs%2C%20FL%2033065!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Progress Insurance Solutions Office Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
